import React, { useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from 'components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { useToast } from 'components/ui/use-toast';
import type { Subject } from 'modules/subject/types';
import { useSubjectList, useCreateSubject } from 'modules/subject';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (subjectId: string) => void;
  isSubmitting?: boolean;
  /** Copy overrides so one picker serves both "create assessment" and "save to bank". */
  title?: string;
  description?: string;
  confirmLabel?: string;
  confirmingLabel?: string;
}

type Mode = 'select' | 'create';

export const CreateAssessmentSubjectModal: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  isSubmitting = false,
  title = 'Choose a subject',
  description = 'Assessments must belong to a subject. Pick an existing one or create a new subject for this assessment.',
  confirmLabel = 'Create assessment',
  confirmingLabel = 'Creating…',
}) => {
  const { toast } = useToast();
  const { data: subjects = [], isLoading } = useSubjectList();
  const createSubject = useCreateSubject();

  const hasSubjects = (subjects as Subject[]).length > 0;
  const [mode, setMode] = useState<Mode>(hasSubjects ? 'select' : 'create');
  const [subjectId, setSubjectId] = useState('');
  const [newName, setNewName] = useState('');

  // When subjects load asynchronously, default to 'create' if none exist yet.
  React.useEffect(() => {
    if (!isLoading && !hasSubjects) {
      setMode('create');
    }
  }, [isLoading, hasSubjects]);

  const handleConfirm = async () => {
    if (mode === 'select') {
      if (!subjectId) {
        toast.error('Select a subject', { description: 'Pick a subject or create a new one.' });
        return;
      }
      onConfirm(subjectId);
      return;
    }

    const trimmed = newName.trim();
    if (!trimmed) {
      toast.error('Name required', { description: 'Enter a name for the new subject.' });
      return;
    }

    try {
      const created = await createSubject.mutateAsync({ name: trimmed });
      if (!created?.id) {
        throw new Error('Subject creation returned no id');
      }
      // Stay in the modal: switch back to "Existing subject" with the
      // freshly created subject pre-selected so the user can review and
      // explicitly confirm before the assessment is created.
      setMode('select');
      setSubjectId(created.id);
      setNewName('');
    } catch {
      // useCreateSubject already toasts the error
    }
  };

  const isBusy = isSubmitting || createSubject.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !isBusy && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {hasSubjects && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMode('select')}
                disabled={isBusy}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  mode === 'select'
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border bg-background text-muted-foreground hover:border-foreground/30'
                }`}
              >
                Existing subject
              </button>
              <button
                type="button"
                onClick={() => setMode('create')}
                disabled={isBusy}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  mode === 'create'
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border bg-background text-muted-foreground hover:border-foreground/30'
                }`}
              >
                New subject
              </button>
            </div>
          )}

          {mode === 'select' ? (
            <div>
              <label className="text-sm font-medium mb-1 block">Subject *</label>
              <Select value={subjectId} onValueChange={setSubjectId} disabled={isBusy}>
                <SelectTrigger>
                  <SelectValue placeholder={isLoading ? 'Loading…' : 'Select a subject'} />
                </SelectTrigger>
                <SelectContent>
                  {(subjects as Subject[]).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} ({s.questionCount} questions)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <label className="text-sm font-medium mb-1 block">New subject name *</label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Biology 101"
                disabled={isBusy}
                maxLength={100}
                autoFocus
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              isBusy ||
              (mode === 'select' ? !subjectId : !newName.trim())
            }
          >
            {isBusy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {mode === 'create' ? 'Creating subject…' : confirmingLabel}
              </>
            ) : mode === 'create' ? (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Create subject
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
